import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import * as signalR from '@microsoft/signalr';
import { AppConfigService } from '../../../../config/app-config.service';
import { AuthService } from '../../../../core/services/auth/auth.service';

export interface SupportMessageEvent {
  conversationId: number;
  companyId: number;
  cid: number;
  messageId: number;
  senderType: string;
  senderId: string;
  messageText: string;
  createdDate: string;
  status: number;
  statusLabel: string;
  attachmentUrl?: string | null;
  attachmentFileName?: string | null;
  attachmentContentType?: string | null;
}

export interface SupportConversationCreatedEvent {
  conversationId: number;
  companyId: number;
  cid: number;
  employeeId: string;
  name: string;
  subject: string | null;
  status: number;
  statusLabel: string;
  messageText: string | null;
  createdDate: string;
}

export interface SupportStatusChangedEvent {
  conversationId: number;
  companyId: number;
  status: number;
  statusLabel: string;
}

@Injectable({ providedIn: 'root' })
export class SupportRealtimeService {
  private hub?: signalR.HubConnection;
  private startPromise?: Promise<void>;

  private readonly joinedCompanies = new Set<number>();
  private readonly joinedConversations = new Set<number>();

  readonly messageReceived$ = new Subject<SupportMessageEvent>();
  readonly conversationCreated$ = new Subject<SupportConversationCreatedEvent>();
  readonly statusChanged$ = new Subject<SupportStatusChangedEvent>();

  constructor(
    private appConfigService: AppConfigService,
    private authService: AuthService
  ) {}

  isConnected(): boolean {
    return this.hub?.state === signalR.HubConnectionState.Connected;
  }

  /** Lazily create + start the connection (idempotent). */
  async connect(): Promise<void> {
    if (this.hub?.state === signalR.HubConnectionState.Connected) return;
    if (this.startPromise) return this.startPromise;

    const token = this.authService.getToken();
    if (!token) {
      console.warn('SupportRealtime: no auth token; skipping hub connection.');
      return;
    }

    const url = `${this.appConfigService.getSupportHubBaseUrl()}/ws-hubs/support-chat`;

    this.hub = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => this.authService.getToken() ?? '',
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    this.hub.on('SupportMessageReceived', (payload: SupportMessageEvent) =>
      this.messageReceived$.next(this.normalizeMessageEvent(payload))
    );
    this.hub.on('SupportConversationCreated', (payload: SupportConversationCreatedEvent) =>
      this.conversationCreated$.next(this.normalizeConversationCreatedEvent(payload))
    );
    this.hub.on('SupportConversationStatusChanged', (payload: SupportStatusChangedEvent) =>
      this.statusChanged$.next(this.normalizeStatusChangedEvent(payload))
    );

    this.hub.onreconnected(async () => {
      await this.rejoinGroups();
    });

    this.hub.onclose(() => {
      this.startPromise = undefined;
    });

    this.startPromise = this.hub
      .start()
      .then(async () => {
        await this.rejoinGroups();
      })
      .catch((err) => {
        console.error('SupportRealtime: connection failed', err);
        this.startPromise = undefined;
        this.hub = undefined;
      });

    return this.startPromise;
  }

  async joinCompany(companyId: number): Promise<void> {
    if (!companyId) return;
    this.joinedCompanies.add(companyId);
    await this.connect();
    await this.invokeJoinCompany(companyId);
  }

  async leaveCompany(companyId: number): Promise<void> {
    this.joinedCompanies.delete(companyId);
    if (this.hub?.state === signalR.HubConnectionState.Connected) {
      await this.hub.invoke('LeaveCompany', companyId).catch(() => {});
    }
  }

  async joinConversation(conversationId: number): Promise<void> {
    if (!conversationId) return;
    this.joinedConversations.add(conversationId);
    await this.connect();
    await this.invokeJoinConversation(conversationId);
  }

  async leaveConversation(conversationId: number): Promise<void> {
    this.joinedConversations.delete(conversationId);
    if (this.hub?.state === signalR.HubConnectionState.Connected) {
      await this.hub.invoke('LeaveConversation', conversationId).catch(() => {});
    }
  }

  private async invokeJoinCompany(companyId: number): Promise<void> {
    if (this.hub?.state !== signalR.HubConnectionState.Connected) return;
    await this.hub.invoke('JoinCompany', companyId).catch((err) => {
      console.warn('SupportRealtime: JoinCompany failed', err);
    });
  }

  private async invokeJoinConversation(conversationId: number): Promise<void> {
    if (this.hub?.state !== signalR.HubConnectionState.Connected) return;
    await this.hub.invoke('JoinConversation', conversationId).catch((err) => {
      console.warn('SupportRealtime: JoinConversation failed', err);
    });
  }

  private async rejoinGroups(): Promise<void> {
    if (this.hub?.state !== signalR.HubConnectionState.Connected) return;

    for (const companyId of this.joinedCompanies) {
      await this.invokeJoinCompany(companyId);
    }
    for (const conversationId of this.joinedConversations) {
      await this.invokeJoinConversation(conversationId);
    }
  }

  private normalizeMessageEvent(payload: SupportMessageEvent): SupportMessageEvent {
    return {
      ...payload,
      conversationId: Number(payload.conversationId),
      companyId: Number(payload.companyId),
      messageId: Number(payload.messageId),
    };
  }

  private normalizeConversationCreatedEvent(
    payload: SupportConversationCreatedEvent
  ): SupportConversationCreatedEvent {
    return {
      ...payload,
      conversationId: Number(payload.conversationId),
      companyId: Number(payload.companyId),
    };
  }

  private normalizeStatusChangedEvent(
    payload: SupportStatusChangedEvent
  ): SupportStatusChangedEvent {
    return {
      ...payload,
      conversationId: Number(payload.conversationId),
      companyId: Number(payload.companyId),
      status: Number(payload.status),
    };
  }
}
